/**
 * 顶部栏组件
 * 
 * 显示角色头像、回合信息和双方独立好感度进度条
 * Layout: [charA avatar] [cpA value] [==cpA bar==>] [heart] [<==cpB bar==] [cpB value] [charB avatar]
 */

import React from 'react';
import { Heart, Settings } from 'lucide-react';
import type { Character } from '../../../types';
import { MAX_TURNS } from '../../../constants';
import { CP_STYLES } from '../../../constants/styles';

interface TopBarProps {
  charA: Character;
  charB: Character;
  turn: number;
  cpA: number;
  cpB: number;
  onSettingsClick?: () => void;
}

export const TopBar: React.FC<TopBarProps> = React.memo(({ charA, charB, turn, cpA, cpB, onSettingsClick }) => {
  const isHighCpA = cpA > 50;
  const isHighCpB = cpB > 50;

  return (
    <div className="bg-white/80 backdrop-blur-md border-b border-[#E5E0D8] flex flex-col z-10 shrink-0">
      {/* Turn counter + settings */}
      <div className="flex items-center justify-center pt-1.5 pb-0.5 relative">
        <span className="text-[10px] font-medium text-[#9CA3AF]">
          第 {turn}/{MAX_TURNS} 轮
        </span>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#F5EDE3] transition-colors text-[#C4B9AC] hover:text-[#6B7280]"
            aria-label="设置"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Dual CP bars */}
      <div className="flex items-center px-3 pb-2 gap-1.5">
        {/* Character A side */}
        <img src={charA.avatar} className="w-7 h-7 rounded-full shrink-0" alt={charA.name} />
        <span className="text-xs font-bold text-[#2D2D2D] w-6 text-right shrink-0">{cpA}</span>

        {/* CP-A progress bar (grows left-to-right) */}
        <div className="flex-1 relative h-2.5 bg-[#F5EDE3] rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full transition-all duration-700 ease-out rounded-full"
            style={{
              width: `${cpA}%`,
              background: isHighCpA ? CP_STYLES.high.gradient : CP_STYLES.low.gradient,
            }}
          />
        </div>

        {/* Heart icon in the center */}
        <Heart
          size={16}
          className={(isHighCpA && isHighCpB) ? CP_STYLES.high.heartColor : CP_STYLES.low.heartColor}
        />

        {/* CP-B progress bar (grows right-to-left) */}
        <div className="flex-1 relative h-2.5 bg-[#F5EDE3] rounded-full overflow-hidden">
          <div
            className="absolute top-0 right-0 h-full transition-all duration-700 ease-out rounded-full"
            style={{
              width: `${cpB}%`,
              background: isHighCpB ? CP_STYLES.high.gradient : CP_STYLES.low.gradient,
            }}
          />
        </div>

        {/* Character B side */}
        <span className="text-xs font-bold text-[#2D2D2D] w-6 text-left shrink-0">{cpB}</span>
        <img src={charB.avatar} className="w-7 h-7 rounded-full shrink-0" alt={charB.name} />
      </div>
    </div>
  );
});

TopBar.displayName = 'TopBar';
